import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { EmptyState } from './shared';
import { Users, Package, MessageSquare, DollarSign, Percent, ExternalLink, Mail, AlertTriangle, RefreshCw, PartyPopper, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

// ─── Skeleton ────────────────────────────────────────────────
function SectionSkeleton({ rows = 3 }) {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={`skel-${i}`} className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
      ))}
    </div>
  );
}

// ─── KPI Card ────────────────────────────────────────────────
function KpiCard({ title, value, suffix, variation, sparkline, icon: Icon, color }) {
  const isPositive = variation > 0;
  const isZero = variation === 0;
  const sparkData = (sparkline || []).map((v, i) => ({ v, i }));
  const sparkColor = isZero ? '#94a3b8' : isPositive ? '#10b981' : '#f59e0b';
  const colors = {
    blue: 'bg-blue-50 text-[#1F3F72] dark:bg-blue-900/30 dark:text-blue-300',
    orange: 'bg-orange-50 text-[#F25C05] dark:bg-orange-900/30 dark:text-orange-300',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4 flex flex-col justify-between" data-testid={`kpi-${title}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        {sparkData.length > 0 && (
          <div className="w-16 h-8">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparkData}>
                <Line type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-neutral-900 dark:text-white">{value}{suffix && <span className="text-sm font-normal text-neutral-400 ml-0.5">{suffix}</span>}</p>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400">{title}</p>
        {!isZero && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isPositive ? '+' : ''}{variation}%
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Funnel Step ─────────────────────────────────────────────
function FunnelStep({ label, value, maxValue, conversionPct, isFirst }) {
  const widthPct = maxValue > 0 ? Math.max((value / maxValue) * 100, 8) : 8;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 sm:w-36 text-right">
        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{label}</p>
        {!isFirst && conversionPct !== null && (
          <p className="text-[10px] text-neutral-400">{conversionPct}% conversion</p>
        )}
      </div>
      <div className="flex-1 relative">
        <div className="h-8 bg-neutral-100 dark:bg-neutral-700 rounded-lg overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#1F3F72] to-[#2d5a9e] rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
            style={{ width: `${widthPct}%` }}
          >
            <span className="text-[11px] font-bold text-white">{value}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────
export function PilotageTab({ setEmailModal, setActiveTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: d } = await api.get('/admin/pilotage');
      setData(d);
    } catch (err) {
      console.error('Pilotage load failed:', err);
      setError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-neutral-600 dark:text-neutral-400 mb-4">Impossible de charger le tableau de bord</p>
        <Button variant="outline" onClick={loadData}><RefreshCw className="w-4 h-4 mr-2" /> Reessayer</Button>
      </div>
    );
  }

  const kpis = data?.kpis;
  const todo = data?.todo;
  const funnel = data?.funnel;
  const funnelMax = funnel ? Math.max(funnel.registrations, funnel.with_listing, funnel.with_message, funnel.with_boost_paid, 1) : 1;
  const funnelConv = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="pilotage-tab">

      {/* ─── SECTION 1: Cette semaine ─── */}
      <div>
        <h2 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">Cette semaine</h2>
        {loading ? <SectionSkeleton rows={2} /> : kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard title="Nouvelles inscriptions" value={kpis.registrations.current_week} variation={kpis.registrations.variation_pct} sparkline={kpis.registrations.sparkline} icon={Users} color="blue" />
            <KpiCard title="Nouvelles annonces" value={kpis.listings.current_week} variation={kpis.listings.variation_pct} sparkline={kpis.listings.sparkline} icon={Package} color="green" />
            <KpiCard title="Nouvelles conversations" value={kpis.conversations.current_week} variation={kpis.conversations.variation_pct} sparkline={kpis.conversations.sparkline} icon={MessageSquare} color="purple" />
            <KpiCard title="Revenus boost" value={kpis.boost_revenue.current_week} suffix="EUR" variation={kpis.boost_revenue.variation_pct} sparkline={kpis.boost_revenue.sparkline} icon={DollarSign} color="orange" />
            <KpiCard title="Taux d'activation" value={kpis.activation_rate.current_week} suffix="%" variation={kpis.activation_rate.variation_pct} sparkline={kpis.activation_rate.sparkline} icon={Percent} color="amber" />
          </div>
        )}
      </div>

      {/* ─── SECTION 2: A faire ─── */}
      <div>
        <h2 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">A faire</h2>
        {loading ? <SectionSkeleton rows={4} /> : todo && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Card A: Inscrits sans annonce */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4" data-testid="todo-no-listing">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#F25C05]" />
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Inscrits sans annonce</h3>
                <span className="ml-auto text-[10px] text-neutral-400">{todo.registered_no_listing.length}</span>
              </div>
              {todo.registered_no_listing.length === 0 ? (
                <div className="text-center py-6">
                  <PartyPopper className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                  <p className="text-xs text-neutral-400">Rien a signaler</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[380px] overflow-y-auto">
                  {todo.registered_no_listing.map(u => (
                    <div key={u.id} className="flex items-center justify-between gap-2 text-xs bg-neutral-50 dark:bg-neutral-700/30 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-medium text-neutral-900 dark:text-white truncate">{u.first_name} {u.last_name}</p>
                        <p className="text-neutral-400 truncate">{u.email}</p>
                        <p className="text-neutral-400">Inscrit il y a {u.days_since}j{u.company_name ? ` — ${u.company_name}` : ''}</p>
                      </div>
                      {setEmailModal && (
                        <Button size="sm" variant="ghost" onClick={() => setEmailModal(u.email)} className="shrink-0 text-[10px] text-[#F25C05]" data-testid={`email-${u.id}`}>
                          <Mail className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Card B: Annonces sans vue */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4" data-testid="todo-no-views">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-[#F25C05]" />
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Annonces sans vue</h3>
                <span className="ml-auto text-[10px] text-neutral-400">{todo.listings_no_views.length}</span>
              </div>
              {todo.listings_no_views.length === 0 ? (
                <div className="text-center py-6">
                  <PartyPopper className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                  <p className="text-xs text-neutral-400">Rien a signaler</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[380px] overflow-y-auto">
                  {todo.listings_no_views.map(l => (
                    <div key={l.id} className="flex items-center justify-between gap-2 text-xs bg-neutral-50 dark:bg-neutral-700/30 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-medium text-neutral-900 dark:text-white truncate">{l.title}</p>
                        <p className="text-neutral-400">{l.price}EUR — {l.sector} — {l.seller_name}</p>
                        <p className="text-neutral-400">{l.views} vues — publiee il y a {l.days_since}j</p>
                      </div>
                      <a href={`/annonce/${l.id}`} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[#F25C05] hover:text-[#d94f04]">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Card C: Conversations bloquees */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4" data-testid="todo-stuck-convs">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#F25C05]" />
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Conversations bloquees</h3>
                <span className="ml-auto text-[10px] text-neutral-400">{todo.stuck_conversations.length}</span>
              </div>
              {todo.stuck_conversations.length === 0 ? (
                <div className="text-center py-6">
                  <PartyPopper className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                  <p className="text-xs text-neutral-400">Rien a signaler</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[380px] overflow-y-auto">
                  {todo.stuck_conversations.map(c => (
                    <div key={c.id} className="text-xs bg-neutral-50 dark:bg-neutral-700/30 rounded-lg px-3 py-2">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">{c.listing_title}</p>
                      <p className="text-neutral-400">{c.buyer_name} → {c.seller_name}</p>
                      <p className="text-neutral-400">Dernier message il y a {c.days_since}j — vendeur n'a pas repondu</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── SECTION 3: Funnel 30 jours ─── */}
      <div>
        <h2 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">Funnel 30 jours</h2>
        {loading ? <SectionSkeleton rows={5} /> : funnel && (
          <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-5 space-y-3">
            <FunnelStep label="Visiteurs" value={funnel.visitors || '—'} maxValue={funnelMax} conversionPct={null} isFirst />
            <FunnelStep label="Inscriptions" value={funnel.registrations} maxValue={funnelMax} conversionPct={null} isFirst />
            <FunnelStep label="Ont publie" value={funnel.with_listing} maxValue={funnelMax} conversionPct={funnelConv(funnel.with_listing, funnel.registrations)} />
            <FunnelStep label="Ont recu un message" value={funnel.with_message} maxValue={funnelMax} conversionPct={funnelConv(funnel.with_message, funnel.with_listing)} />
            <FunnelStep label="Ont paye un boost" value={funnel.with_boost_paid} maxValue={funnelMax} conversionPct={funnelConv(funnel.with_boost_paid, funnel.with_message)} />
            <p className="text-[10px] text-neutral-400 pt-2 border-t dark:border-neutral-700">Les visiteurs seront disponibles apres integration Google Analytics.</p>
          </div>
        )}
      </div>
    </div>
  );
}
